import { getFirestore, doc, deleteDoc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { dataApp } from '../config/firebase';
import { Product } from '../types/product';
import { productVersionService } from './ProductVersionService';

const db = getFirestore(dataApp);

class ProductService {
  private static instance: ProductService;
  private readonly productsCollection = 'products';

  private constructor() {}

  static getInstance(): ProductService {
    if (!ProductService.instance) {
      ProductService.instance = new ProductService();
    }
    return ProductService.instance;
  }

  async createProduct(product: Product): Promise<void> {
    try {
      const productRef = doc(db, this.productsCollection, product.id);
      await setDoc(productRef, {
        ...product,
        timestamps: {
          created: new Date(),
          updated: new Date(),
          imported: new Date()
        }
      });

      // Create initial version
      await productVersionService.createVersion(product, 'import');
    } catch (error) {
      throw error;
    }
  }

  private async ensureInitialVersion(product: Product): Promise<void> {
    try {
      const versions = await productVersionService.getVersions(product.id);
      if (versions.length === 0) {
        // Create initial version if none exists
        await productVersionService.createVersion(product, 'import');
      }
    } catch (error) {
      // Don't throw - we want to continue even if version creation fails
    }
  }

  async updateProduct(productId: string, updates: Partial<Product>, versionType: 'optimization' | 'manual' = 'manual', marketplace?: 'shopify' | 'amazon' | 'googleShopping'): Promise<void> {
    try {
      const productRef = doc(db, this.productsCollection, productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }

      const currentProduct = productSnap.data() as Product;
      
      // Ensure initial version exists
      await this.ensureInitialVersion(currentProduct);

      const updatedProduct = {
        ...currentProduct,
        ...updates,
        timestamps: {
          ...currentProduct.timestamps,
          updated: new Date()
        }
      };

      await updateDoc(productRef, updatedProduct);

      // Create new version
      await productVersionService.createVersion(
        updatedProduct,
        versionType,
        marketplace
      );
    } catch (error) {
      throw error;
    }
  }

  async updatePublishTimestamp(productId: string, marketplace: 'shopify' | 'amazon' | 'googleShopping'): Promise<void> {
    try {
      const productRef = doc(db, this.productsCollection, productId);
      await updateDoc(productRef, {
        [`publishTimestamps.${marketplace}`]: new Date(),
        'timestamps.updated': new Date()
      });
    } catch (error) {
      throw error;
    }
  }

  async restoreVersion(versionId: string): Promise<void> {
    try {
      const version = await productVersionService.getVersion(versionId);
      if (!version) {
        throw new Error('Version not found');
      }

      const productRef = doc(db, this.productsCollection, version.productId);
      const productDoc = await getDoc(productRef);
      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }

      const currentProduct = productDoc.data() as Product;

      // If this is Version 1 (Initial Import), only restore the basicInfo
      if (version.type === 'import' && version.versionNumber === 1) {
        const restoredProduct = {
          ...currentProduct,
          basicInfo: version.data.basicInfo,
          timestamps: {
            ...currentProduct.timestamps,
            updated: new Date()
          }
        };
        await updateDoc(productRef, restoredProduct);
      } else {
        // For other versions, restore everything
        const restoredProduct = {
          ...currentProduct,
          ...version.data,
          id: version.productId,
          timestamps: {
            ...currentProduct.timestamps,
            updated: new Date()
          }
        };
        await updateDoc(productRef, restoredProduct);
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteProduct(productId: string): Promise<void> {
    try {
      const productRef = doc(db, this.productsCollection, productId);
      await deleteDoc(productRef);
    } catch (error) {
      throw error;
    }
  }

  async deleteProducts(productIds: string[]): Promise<void> {
    try {
      await Promise.all(
        productIds.map(id => this.deleteProduct(id))
      );
    } catch (error) {
      throw error;
    }
  }
}

export const productService = ProductService.getInstance();
